import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Link2, CheckCircle2, Loader2, ArrowRight, ShieldCheck, Info, Unlink,
} from "lucide-react";

/**
 * IBKR MCP connection card — connect via IBKR's native OAuth (no droplet, no gateway).
 * Drop into Settings page or anywhere else.
 */
export default function IBKRConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/ibkr/mcp/status").then((r) => setStatus(r.data))
      .catch(() => setStatus({ connected: false }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // If we just returned from IBKR OAuth, show a toast
    const p = new URLSearchParams(window.location.search);
    if (p.get("ibkr") === "connected") toast.success("IBKR connected — Aanar can now read your portfolio and draft trades");
    if (p.get("ibkr") === "error") toast.error("Couldn't connect to IBKR: " + (p.get("reason") || "unknown"));
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/ibkr/mcp/authorize");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error("Couldn't start IBKR connection");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Aanar from IBKR? You can reconnect any time.")) return;
    setBusy(true);
    try {
      await api.post("/ibkr/mcp/disconnect");
      toast.success("Disconnected from IBKR");
      refresh();
    } catch { toast.error("Couldn't disconnect"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 rounded-2xl" data-testid="ibkr-mcp-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[hsl(var(--ai))]/10 border border-[hsl(var(--ai))]/30 flex items-center justify-center shrink-0">
          <Link2 className="w-5 h-5 text-[hsl(var(--ai))]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-bold text-lg">Interactive Brokers</div>
            {status?.connected && (
              <Badge className="bg-[hsl(var(--gain))]/15 text-[hsl(var(--gain))] border-[hsl(var(--gain))]/40">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Aanar reads your live positions and drafts every trade straight into IBKR's
            <span className="text-foreground font-semibold"> AI Instructions</span> tab —
            you 1-tap confirm before it goes live. It's IBKR-native (no gateway, no
            manual token dance), open to every MCP client since Aug&nbsp;2026.
          </p>

          {loading ? (
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking status…
            </div>
          ) : status?.connected ? (
            <div className="mt-3 space-y-2">
              <div className="text-xs text-muted-foreground">
                Connected since <span className="text-foreground font-mono-tab">
                  {new Date(status.connected_at).toLocaleDateString()}
                </span>
                {status.ibkr_account_id && (
                  <> · Account <span className="text-foreground font-mono-tab">{status.ibkr_account_id}</span></>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={disconnect} disabled={busy}
                      data-testid="ibkr-disconnect-btn" className="rounded-full">
                <Unlink className="w-3.5 h-3.5 mr-1.5" /> Disconnect
              </Button>
            </div>
          ) : (
            <Button onClick={connect} disabled={busy} data-testid="ibkr-connect-btn"
                    className="mt-3 rounded-full bg-[hsl(var(--ai))] hover:bg-[hsl(var(--ai))]/90 text-background">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              Connect IBKR account
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}

          {/* Trust bullets */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            {[
              { icon: ShieldCheck, t: "Aanar never sees your password", d: "You log in on IBKR's own screen (OAuth 2.1 + PKCE)." },
              { icon: CheckCircle2, t: "1-tap confirm live trades", d: "Every draft appears in IBKR's AI Instructions tab." },
              { icon: Info, t: "Revocable any time", d: "Client Portal → Manage Third-Party Consents." },
            ].map((b, i) => {
              const I = b.icon;
              return (
                <div key={i} className="p-2.5 rounded-xl bg-secondary/40">
                  <I className="w-3.5 h-3.5 text-[hsl(var(--ai))] mb-1" />
                  <div className="font-semibold text-foreground">{b.t}</div>
                  <div className="text-muted-foreground mt-0.5">{b.d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}
